import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { AuthenticationResult, BaseAuthRequest } from '@azure/msal-common';
import { defer, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DataUtil } from '@celum/core/util';

import { LoginRedirectService } from './services/login-redirect.service';
import { addCustomDomainStateToRequestIfNecessary } from './utils/custom-domain.util';

export function tokenProviders(
  client: MsalService,
  request: BaseAuthRequest,
  loginRedirectService: LoginRedirectService,
  silent = false
): Observable<AuthenticationResult>[] {
  // Tries to acquire a token silently in the background without user interaction.
  // This will work if the user has a valid access token or their refresh token is still valid (which can fetch a new access token without login).
  const silentTokenAcquisition = defer(() => attemptSilentTokenAcquisition(client, request));

  // Redirects the user to the login page. Should only be done if a token could not be acquired silently.
  // This can happen if there is no auth information available, or the refresh token is expired (24h).
  const redirectTokenAcquisition = defer(() => acquireTokenWithRedirect(client, request, loginRedirectService));

  return silent ? [silentTokenAcquisition] : [silentTokenAcquisition, redirectTokenAcquisition];
}

export function isInteractiveLoginRequired(client: MsalService, request: BaseAuthRequest): Observable<boolean> {
  return acquireTokenSilently(client, request).pipe(
    map(() => false),
    catchError(error => {
      if (error instanceof InteractionRequiredAuthError) {
        return of(true);
      }
      throw error;
    })
  );
}

function attemptSilentTokenAcquisition(client: MsalService, request: BaseAuthRequest): Observable<AuthenticationResult> {
  return acquireTokenSilently(client, request).pipe(
    catchError(error => {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('TokenProvider: Could not acquire token silently - Re-login required');
      }
      return of(null);
    })
  );
}

function acquireTokenSilently(client: MsalService, request: BaseAuthRequest): Observable<AuthenticationResult> {
  const customDomainReadyRequest = addCustomDomainStateToRequestIfNecessary(request);
  return client.acquireTokenSilent(customDomainReadyRequest);
}

function acquireTokenWithRedirect(client: MsalService, request: BaseAuthRequest, loginRedirectService: LoginRedirectService): Observable<AuthenticationResult> {
  // We will redirect the user to the login page and therefore need to store the current URL state.
  // Once we are logged in and navigated back, the AuthGuard will redirect to the stored URL again.
  // If we weren't doing this, the user would stay on the logged-in page and nothing else would happen.
  storeCurrentUrlAsRedirectUrlIfNeeded(loginRedirectService);

  const customDomainReadyRequest = addCustomDomainStateToRequestIfNecessary(request);
  return client.acquireTokenRedirect(customDomainReadyRequest).pipe(map(() => null as AuthenticationResult));
}

function storeCurrentUrlAsRedirectUrlIfNeeded(loginRedirectService: LoginRedirectService): void {
  const storedRedirectUri = loginRedirectService.getLoginRedirectUri();
  const currentCleanedUrl = loginRedirectService.cleanRedirectUri(window.location.pathname + window.location.search);
  // Store the redirectUri if there is non yet, or the user e.g. clicked on a link to a subpage while they are logged out.
  // Avoid overriding a deeplink with '/' - this would happen after the initial login
  if (DataUtil.isEmpty(storedRedirectUri) || (storedRedirectUri !== currentCleanedUrl && currentCleanedUrl !== '/')) {
    loginRedirectService.storeLoginRedirectUri(currentCleanedUrl);
  }
}
